import { useEffect } from 'react';

import { useMaybeDrfqHistoryItem } from '@paradigm/features/src/unified-rfqs/contexts/drfqs';
import { useApi } from '@paradigm/api/src';
import { Drfq } from '#/unified-rfqs/entities/drfq/domain';
import { useDrfqService } from '#/unified-rfqs/contexts/drfqs';
import { useRfqContext } from '#/unified-rfqs/ui/rfq-context';

/**
 * Get the rfq efficiently.
 *
 * WARNING: This hook should only be used with DRFQ ids, so the
 * rfq.type is "drfq"
 *
 * This hook will get the rfq using the network if necessary.
 * It will fetch the rfq for a given `rfqId` through the network
 * only once. Before fetching, it retrieves the rfq from the RfqStore or
 * from RfqHistoryStore.
 *
 * @param rfqId
 * @returns the drfq data and loading indicator
 */
export default function useFetchedDrfq(rfqId: Drfq['id']) {
  const drfqService = useDrfqService();
  const [{ isLoading }, getDrfq] = useApi(drfqService.getDrfq);
  const { rfq } = useRfqContext();
  const rfqHistoryItem = useMaybeDrfqHistoryItem(rfqId);
  const storedRfq = rfq || rfqHistoryItem;

  useEffect(() => {
    if (storedRfq) return;

    getDrfq({ rfqId });
  }, [getDrfq, rfqId, storedRfq]);

  if (storedRfq != null && storedRfq.type !== 'drfq') {
    throw new TypeError(`Rfq ${rfqId} is not of kind drfq`);
  }

  return { isLoading, drfq: storedRfq };
}
